import React from "react";
import { styled } from "@mui/material/styles";
import { TableHead, TableRow, TableCell, Skeleton, TextField } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import colors from "assets/theme-dark/base/colors";
import { AnalysisDriverType } from "../../types/types";
import TruncatedText from "../TruncatedText";
import { formatDriverHeader } from "../../utils/excel/createHeaders";
import { DriverTableSegment } from "../../utils/driverTableUtils";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import { cruxyColors } from "../../theme/color-palette";

interface DriverMetricsTableHeaderProps {
  segments: DriverTableSegment[];
  driverType: string;
  driverMode: AnalysisDriverType;
  driverNumeratorType: string;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  handleSort: (column: string) => void;
  hasOneDataAndOneManualSegment: boolean;
  isSkeleton: boolean;
}

const DriverMetricsTableHeader: React.FC<DriverMetricsTableHeaderProps> = ({
  segments,
  driverType,
  driverMode,
  driverNumeratorType,
  sortColumn,
  sortDirection,
  handleSort,
  hasOneDataAndOneManualSegment,
  isSkeleton,
}) => {
  const { headroomDriver, setHeadroomDriverPercentile, headroomDriverPercentile } = useHeadroomContext();
  const [localPercentile, setLocalPercentile] = React.useState(headroomDriverPercentile.toString());

  const formatSubheader = (
    driverNumber: number,
    type: string,
    numeratorType: string,
    mode: AnalysisDriverType,
    suffix: string
  ) => {
    const header = formatDriverHeader(driverNumber, type, numeratorType, mode);
    return header.replace(/^Driver \d+ - /, "") + ` - ${suffix}`;
  };

  const formatSegmentHeader = (segment: DriverTableSegment) => {
    return `Segment ${segment.index + 1} - ${segment.segmentType} - ${segment.method}`;
  };

  const driverNumber = headroomDriver === "driver1" ? 1 : 2;
  const medianSortColumn = headroomDriver === "driver1" ? "driver1Median" : "driver2Median";
  const avgSortColumn = headroomDriver === "driver1" ? "driver1Avg" : "driver2Avg";

  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");

  React.useEffect(() => {
    setLocalPercentile(headroomDriverPercentile.toString());
  }, [headroomDriverPercentile]);

  const handlePercentileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === "") {
      setLocalPercentile("");
      return;
    }

    if (/^\d+$/.test(value)) {
      const numValue = parseInt(value);
      if (numValue >= 1 && numValue <= 100) {
        setLocalPercentile(value);
      }
    }
  };

  const handlePercentileBlur = () => {
    const parsedValue = parseInt(localPercentile);
    if (isNaN(parsedValue) || localPercentile.trim() === "") {
      setHeadroomDriverPercentile(1);
      setLocalPercentile("1");
    } else {
      const boundedPercentile = Math.max(1, Math.min(100, parsedValue));
      setHeadroomDriverPercentile(boundedPercentile);
      setLocalPercentile(boundedPercentile.toString());
    }
  };

  if (isSkeleton) {
    const skeletonColumnCount =
      isManualOnly || isDataOnly || hasOneDataAndOneManualSegment ? 5 : 2 + segments.length * 2 + 1;
    return (
      <TableHead>
        <TableRow>
          {Array(skeletonColumnCount)
            .fill(null)
            .map((_, index) => (
              <TableCell key={index} sx={{ border: `1px solid ${colors.grey[300]}`, padding: 1.5 }}>
                <Skeleton variant="rectangular" height={32} />
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  }

  const segmentsColSpan =
    segments.length === 0 ? 2 : isManualOnly || isDataOnly || hasOneDataAndOneManualSegment ? 2 : segments.length * 2;

  if (isManualOnly || isDataOnly) {
    return (
      <TableHead>
        <TableRow>
          <HeaderTableCell colSpan={2} align="center">
            Segments
          </HeaderTableCell>
          <HeaderTableCell colSpan={3} align="center">
            <TruncatedText
              text={formatDriverHeader(driverNumber, driverType, driverNumeratorType, driverMode)}
              maxLength={60}
            />
          </HeaderTableCell>
        </TableRow>
        <TableRow>
          <SubHeaderTableCell align="center">{isManualOnly ? "Manual" : "Data"}</SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort("segmentValue")}>
              <TruncatedText text={`${segments[0].segmentType} - ${isManualOnly ? "Manual" : "Data"}`} maxLength={30} />
              {sortColumn === "segmentValue" &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort(medianSortColumn)}>
              <TruncatedText
                text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Median")}
                maxLength={40}
              />
              {sortColumn === medianSortColumn &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort(avgSortColumn)}>
              <TruncatedText
                text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Mean")}
                maxLength={40}
              />
              {sortColumn === avgSortColumn &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <TextField
              type="number"
              label="Percentile"
              value={localPercentile}
              onChange={handlePercentileChange}
              onBlur={handlePercentileBlur}
              inputProps={{ min: 1, max: 100, step: 1 }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px",
                  fontSize: "0.75rem",
                  backgroundColor: "transparent",
                  width: "130px",
                  maxWidth: "175px",
                  color: colors.grey[900],
                  textAlign: "center",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[400],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[500],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary.main,
                  borderWidth: "2px",
                },
              }}
            />
          </SubHeaderTableCell>
        </TableRow>
      </TableHead>
    );
  }

  if (hasOneDataAndOneManualSegment) {
    return (
      <TableHead>
        <TableRow>
          <HeaderTableCell colSpan={2} align="center">
            Segments
          </HeaderTableCell>
          <HeaderTableCell colSpan={3} align="center">
            <TruncatedText
              text={formatDriverHeader(driverNumber, driverType, driverNumeratorType, driverMode)}
              maxLength={30}
            />
          </HeaderTableCell>
        </TableRow>
        <TableRow>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort("range")}>
              <TruncatedText
                text={`${segments.find((seg) => seg.method === "MANUAL")?.segmentType || "Manual"} - Manual`}
                maxLength={30}
              />
              {sortColumn === "range" &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort("segmentValue")}>
              <TruncatedText
                text={`${segments.find((seg) => seg.method === "DATA")?.segmentType || "Data"} - Data`}
                maxLength={30}
              />
              {sortColumn === "segmentValue" &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort(medianSortColumn)}>
              <TruncatedText
                text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Median")}
                maxLength={40}
              />
              {sortColumn === medianSortColumn &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort(avgSortColumn)}>
              <TruncatedText
                text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Mean")}
                maxLength={40}
              />
              {sortColumn === avgSortColumn &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <TextField
              type="number"
              label="Percentile"
              value={localPercentile}
              onChange={handlePercentileChange}
              onBlur={handlePercentileBlur}
              inputProps={{ min: 1, max: 100, step: 1 }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px",
                  fontSize: "0.75rem",
                  backgroundColor: "transparent",
                  width: "130px",
                  maxWidth: "175px",
                  color: colors.grey[900],
                  textAlign: "center",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[400],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[500],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary.main,
                  borderWidth: "2px",
                },
              }}
            />
          </SubHeaderTableCell>
        </TableRow>
      </TableHead>
    );
  }

  if (isTwoSameMethod) {
    return (
      <TableHead>
        <TableRow>
          <HeaderTableCell colSpan={2} align="center">
            Segments
          </HeaderTableCell>
          <HeaderTableCell colSpan={3} align="center">
            <TruncatedText
              text={formatDriverHeader(driverNumber, driverType, driverNumeratorType, driverMode)}
              maxLength={30}
            />
          </HeaderTableCell>
        </TableRow>
        <TableRow>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort("range1")}>
              <TruncatedText text={formatSegmentHeader(segments[0])} maxLength={30} />
              {sortColumn === "range1" &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort("range2")}>
              <TruncatedText text={formatSegmentHeader(segments[1])} maxLength={30} />
              {sortColumn === "range2" &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort(medianSortColumn)}>
              <TruncatedText
                text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Median")}
                maxLength={40}
              />
              {sortColumn === medianSortColumn &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton onClick={() => handleSort(avgSortColumn)}>
              <TruncatedText
                text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Mean")}
                maxLength={40}
              />
              {sortColumn === avgSortColumn &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                ))}
            </SortButton>
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <TextField
              type="number"
              label="Percentile"
              value={localPercentile}
              onChange={handlePercentileChange}
              onBlur={handlePercentileBlur}
              inputProps={{ min: 1, max: 100, step: 1 }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px",
                  fontSize: "0.75rem",
                  backgroundColor: "transparent",
                  width: "130px",
                  maxWidth: "175px",
                  color: colors.grey[900],
                  textAlign: "center",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[400],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[500],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary.main,
                  borderWidth: "2px",
                },
              }}
            />
          </SubHeaderTableCell>
        </TableRow>
      </TableHead>
    );
  }

  return (
    <TableHead>
      <TableRow>
        <HeaderTableCell colSpan={segmentsColSpan} align="center">
          Segments
        </HeaderTableCell>
        <HeaderTableCell colSpan={3} align="center">
          <TruncatedText
            text={formatDriverHeader(driverNumber, driverType, driverNumeratorType, driverMode)}
            maxLength={30}
          />
        </HeaderTableCell>
      </TableRow>
      <TableRow>
        {segments.length === 0 ? (
          <SubHeaderTableCell colSpan={2} align="center">
            No Segments Defined
          </SubHeaderTableCell>
        ) : hasOneDataAndOneManualSegment ? (
          <>
            <SubHeaderTableCell align="center">
              <SortButton onClick={() => handleSort("range")}>
                <TruncatedText
                  text={`${segments.find((seg) => seg.method === "MANUAL")?.segmentType || "Manual"} - Manual`}
                  maxLength={30}
                />
                {sortColumn === "range" &&
                  (sortDirection === "asc" ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  ))}
              </SortButton>
            </SubHeaderTableCell>
            <SubHeaderTableCell align="center">
              <SortButton onClick={() => handleSort("segmentValue")}>
                <TruncatedText
                  text={`${segments.find((seg) => seg.method === "DATA")?.segmentType || "Data"} - Data`}
                  maxLength={30}
                />
                {sortColumn === "segmentValue" &&
                  (sortDirection === "asc" ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  ))}
              </SortButton>
            </SubHeaderTableCell>
          </>
        ) : (
          segments.map((seg) => (
            <React.Fragment key={`segment-header-${seg.index}`}>
              <SubHeaderTableCell colSpan={2} align="center">
                <SortButton
                  onClick={() =>
                    handleSort(seg.method === "DATA" ? `segmentValue${seg.index + 1}` : `range${seg.index + 1}`)
                  }
                >
                  <TruncatedText text={formatSegmentHeader(seg)} maxLength={35} />
                  {sortColumn === (seg.method === "DATA" ? `segmentValue${seg.index + 1}` : `range${seg.index + 1}`) &&
                    (sortDirection === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    ))}
                </SortButton>
              </SubHeaderTableCell>
            </React.Fragment>
          ))
        )}
        <SubHeaderTableCell align="center">
          <SortButton onClick={() => handleSort(medianSortColumn)}>
            <TruncatedText
              text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Median")}
              maxLength={40}
            />
            {sortColumn === medianSortColumn &&
              (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
          </SortButton>
        </SubHeaderTableCell>
        <SubHeaderTableCell align="center">
          <SortButton onClick={() => handleSort(avgSortColumn)}>
            <TruncatedText
              text={formatSubheader(driverNumber, driverType, driverNumeratorType, driverMode, "Mean")}
              maxLength={40}
            />
            {sortColumn === avgSortColumn &&
              (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
          </SortButton>
        </SubHeaderTableCell>
        <SubHeaderTableCell align="center">
          <TextField
            type="number"
            label="Percentile"
            value={localPercentile}
            onChange={handlePercentileChange}
            onBlur={handlePercentileBlur}
            inputProps={{ min: 1, max: 100, step: 1 }}
            size="small"
            sx={{
              "& .MuiInputBase-input": {
                padding: "8px",
                fontSize: "0.75rem",
                backgroundColor: "transparent",
                width: "130px",
                maxWidth: "175px",
                color: colors.grey[900],
                textAlign: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.grey[400],
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.grey[500],
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.primary.main,
                borderWidth: "2px",
              },
            }}
          />
        </SubHeaderTableCell>
      </TableRow>
    </TableHead>
  );
};

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.85rem",
  backgroundColor: cruxyColors.blue.main,
  color: colors.white.main,
  fontWeight: "bold",
}));

const SubHeaderTableCell = styled(TableCell)(({ theme }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.75rem",
  backgroundColor: cruxyColors.green.main,
  color: colors.text.primary,
}));

const SortButton = styled("button")(({ theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.5),
  color: colors.text.primary,
  fontSize: "0.75rem",
  fontWeight: "bold",
  padding: theme.spacing(0.5),
  width: "100%",
  textAlign: "center",
}));

export default DriverMetricsTableHeader;
